import { useCoworkerProfileStore } from '@/store/coworker-preferences.store';
import Checkbox from '@ingka/checkbox';
import FormField from '@ingka/form-field';
import Loading, { LoadingBall } from '@ingka/loading';
import Select, { OptGroup, Option } from '@ingka/select';
import { translate } from '@repo/phrase';
import type { AssetDataType, AssetType } from '@repo/types/asset';

import type { ShareOption } from '@/components/share/share-options';
import { useTermsAndConditions } from '@/components/share/share.hooks.ts';

import { useDefaultPrinter, useFavouritePrinterList, usePrinters } from '@/hooks/asset.hooks.ts';
import { getAppConfig } from '@/utils/window.utils.ts';

import styles from './styles.module.scss';

interface Props {
    data: AssetDataType;
    assetType: AssetType;
    printer: ShareOption;
    setPrinter: (printer: ShareOption) => void;
    shouldValidate: boolean;
}

export const PrintShareOption = ({ printer, setPrinter, shouldValidate, assetType, data }: Props) => {
    const config = getAppConfig();
    const coworkerPreferences = useCoworkerProfileStore((state) => state.coworkerPreferences);
    const msalProfile = useCoworkerProfileStore((state) => state.msalProfile);
    const buCode = coworkerPreferences?.buCode;
    const mailNickname = msalProfile?.mailNickname;
    const printEnabled = config.shareOptions.printEnabledFor.includes(assetType);
    const printersQueryEnabled = printEnabled && Boolean(buCode);
    const favoritePrinterQueriesEnabled = printersQueryEnabled && Boolean(mailNickname);

    const showPrintTermsAndConditions = useTermsAndConditions(data);

    const { data: printers, isLoading: printersLoading } = usePrinters(buCode ?? '', {
        enabled: printersQueryEnabled,
    });

    const { data: favoritePrinterList, isLoading: favoritesLoading } = useFavouritePrinterList(
        buCode ?? '',
        mailNickname ?? '',
        { enabled: favoritePrinterQueriesEnabled }
    );
    const { data: defaultPrinter, isLoading: defaultLoading } = useDefaultPrinter(buCode ?? '', mailNickname ?? '', {
        enabled: favoritePrinterQueriesEnabled,
    });

    if (!printEnabled) {
        return null;
    }

    if (printersLoading || favoritesLoading || defaultLoading) {
        return (
            <Loading>
                <LoadingBall color="primary" />
            </Loading>
        );
    }

    return (
        <div data-testid="print-share-option">
            <div className={styles.form_group}>
                <Checkbox
                    id="print-check"
                    data-testid="print-check"
                    name={translate('share_option_print')}
                    aria-label={translate('share_option_print')}
                    value="print"
                    subtle
                    checked={printer.checked}
                    onChange={() => {
                        setPrinter({ ...printer, checked: !printer.checked });
                    }}
                />
                <div className={styles.print_options_row}>
                    <FormField
                        data-testid="copies-select"
                        className={String(styles.form_field)}
                        shouldValidate={printer.checked && shouldValidate}
                        valid={Boolean(printer.noOfCopies)}
                        validation={{
                            msg: translate('form_field_msg_required_field'),
                            id: 'error',
                            type: 'error',
                        }}
                    >
                        <Select
                            id="copiesSelect"
                            data-testid="copies-select"
                            value={String(printer.noOfCopies)}
                            describedById={translate('share_option_copies')}
                            label={translate('share_option_copies')}
                            aria-label={translate('share_option_copies')}
                            onChange={(e) => {
                                setPrinter({ ...printer, noOfCopies: Number(e.target.value) });
                            }}
                            disabled={!printer.checked}
                        >
                            {[...Array(10).keys()].map((i) => (
                                <Option key={i} value={String(i + 1)} name={String(i + 1)} />
                            ))}
                        </Select>
                    </FormField>
                    <FormField
                        className={String(styles.form_field)}
                        shouldValidate={printer.checked && shouldValidate}
                        valid={Boolean(printer.value)}
                        validation={{
                            msg: translate('form_field_msg_required_field'),
                            id: 'error',
                            type: 'error',
                        }}
                    >
                        <Select
                            id="printerSelect"
                            data-testid="printer-select"
                            value={printer.value ?? ''}
                            describedById={translate('share_option_printer')}
                            label={translate('share_option_printer')}
                            aria-label={translate('share_option_printer')}
                            className={String(styles.form_select)}
                            onChange={(e) => {
                                setPrinter({ ...printer, value: e.target.value });
                            }}
                            disabled={!printer.checked}
                        >
                            {defaultPrinter?.defaultPrinter.name && defaultPrinter.defaultPrinter.serverName && (
                                <OptGroup groupName={translate('share_option_printer_default_printer_group_name')}>
                                    <Option
                                        key="default-printer"
                                        value={`${defaultPrinter.defaultPrinter.serverName}|${defaultPrinter.defaultPrinter.name}`}
                                        name={defaultPrinter.defaultPrinter.name}
                                    />
                                </OptGroup>
                            )}

                            {favoritePrinterList?.favoritePrinters.length && (
                                <OptGroup groupName={translate('share_option_printer_favourites_group_name')}>
                                    {favoritePrinterList.favoritePrinters.map((printer) => (
                                        <Option
                                            key={`fp-${printer.serverName}-${printer.name}`}
                                            value={`${printer.serverName}|${printer.name}`}
                                            name={printer.name}
                                        />
                                    ))}
                                </OptGroup>
                            )}

                            <OptGroup groupName={translate('share_option_printer_all_printers_group_name')}>
                                {printers?.printers &&
                                    Object.entries(printers.printers).map(([key, value]) =>
                                        value.map((printer) => (
                                            <Option
                                                key={`option-${printer.serverName}-${printer.name}`}
                                                value={`${printer.serverName}|${printer.name}`}
                                                name={`${printer.name}${key === 'smartPrinters' ? ' (Smart)' : ''}`}
                                            />
                                        ))
                                    )}
                            </OptGroup>
                        </Select>
                    </FormField>
                </div>
            </div>
            {showPrintTermsAndConditions && (
                <FormField className={String(styles.print_terms_row)}>
                    <Checkbox
                        id="print-terms-and-conditions"
                        data-testid="print-terms-and-conditions"
                        value="print-terms"
                        checked={Boolean(printer.printTermsAndConditions)}
                        disabled={!printer.checked}
                        subtle
                        label={translate('print_terms_and_conditions')}
                        name={translate('print_terms_and_conditions')}
                        aria-label={translate('print_terms_and_conditions')}
                        onChange={(e) => {
                            setPrinter({ ...printer, printTermsAndConditions: e.target.checked });
                        }}
                    />
                </FormField>
            )}
        </div>
    );
};
